import React, { useState, useEffect } from "react";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Popconfirm } from "antd";
import { useSelector } from "react-redux";
import TextPreview from "./PreviewComponents/TextPreview";
import BulletListPreview from "./PreviewComponents/BulletListPreview";
import CardGridPreview from "./PreviewComponents/CardGridPreview";
import ProgressBarPreview from "./PreviewComponents/ProgressBarPreview";
import MetricsPreview from "./PreviewComponents/MetricsPreview";
import StatusPreview from "./PreviewComponents/StatusPreview";
import UploadDocumentsPreview from "./PreviewComponents/UploadDocumentsPreview";
import "../DealApproval.scss";
import CustomButton from "../../../components/CustomButton/Button";

const CreatedSectionView = ({
    onAddSection,
    onProceed,
    onEditContent,
    onDeleteContent,
    sections = [],
    activeSectionId,
    onActiveSectionChange,
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const { options = [], dealSectionOptions = {} } = useSelector((state) => state.dealApproval);
    const activeSection = sections[activeTab] || null;
    const allOptions = Array.isArray(dealSectionOptions?.allOptions) && dealSectionOptions.allOptions.length
        ? dealSectionOptions.allOptions
        : options;
    const progressBarColorOptions = allOptions.filter((option) => String(option?.optionType || "").toUpperCase() === "BULLET_COLOR");

    const normalizeContentItems = (section) => {
        if (!section) return [];

        if (typeof section === "string") {
            return [{ title: "Success Metrics", blockType: "Metrics", blocks: [], bulletColor: "grey" }];
        }

        if (Array.isArray(section?.contents) && section.contents.length > 0) {
            return section.contents;
        }

        if (section?.title && section?.blockType) {
            return [
                {
                    title: section.title,
                    blockType: section.blockType,
                    blocks: section.blocks || [],
                    bulletColor: section.bulletColor,
                },
            ];
        }

        return [];
    };

    const activeContents = normalizeContentItems(activeSection).map((item, contentIndex) => ({
        ...item,
        __contentIndex: contentIndex,
    }));

    const groupedByTitle = activeContents.reduce((acc, contentItem) => {
        const groupTitle = contentItem?.title || "Success Metrics";
        const existingGroup = acc.find((group) => group.title === groupTitle);

        if (existingGroup) {
            existingGroup.items.push(contentItem);
            return acc;
        }

        acc.push({ title: groupTitle, items: [contentItem] });
        return acc;
    }, []);

    const renderBlockContent = (contentItem, bulletStartIndex = 0) => {
        const blockType = contentItem?.blockType;
        const blockItems = contentItem?.blocks || [];
        const bulletColor = contentItem?.bulletColor || "";

        switch (blockType) {
            case "Text":
                return <TextPreview blockItems={blockItems} />;

            case "Bullet list":
                return (
                    <BulletListPreview
                        blockItems={blockItems}
                        bulletColor={bulletColor}
                        colorOptions={progressBarColorOptions}
                        startIndex={bulletStartIndex}
                    />
                );

            case "Card grid":
                return <CardGridPreview blockItems={blockItems} colorOptions={progressBarColorOptions} />;

            case "Progress bar":
                return <ProgressBarPreview blockItems={blockItems} colorOptions={progressBarColorOptions} />;

            case "Metrics":
                return <MetricsPreview blockItems={blockItems} />;

            case "Status":
                return <StatusPreview blockItems={blockItems} />;

            case "Upload documents":
                return <UploadDocumentsPreview blockItems={blockItems} />;

            default:
                return null;
        }
    };

    useEffect(() => {
        if (!sections.length) {
            setActiveTab(0);
            return;
        }

        const activeIndex = sections.findIndex((section) => section?.sectionId === activeSectionId);
        setActiveTab(activeIndex >= 0 ? activeIndex : 0);
    }, [sections, activeSectionId]);

    return (
        <section className="panel-card created-section-card">
            {sections.length > 0 && (
                <div className="section-tabs-bar">
                    {sections.map((sec, idx) => (
                        <button
                            key={typeof sec === "string" ? `${sec}-${idx}` : (sec?.sectionId || idx)}
                            type="button"
                            className={`section-tab-btn${activeTab === idx ? " active" : ""}`}
                            onClick={() => {
                                setActiveTab(idx);
                                if (typeof sec !== "string") {
                                    onActiveSectionChange?.(sec?.sectionId || null);
                                }
                            }}
                        >
                            {typeof sec === "string" ? sec : sec.sectionName}
                        </button>
                    ))}
                </div>
            )}

            <div className="created-block">
                {groupedByTitle.map((group, groupIndex) => (
                    <div key={`${group.title}-${groupIndex}`}>
                        <h4>{group.title}</h4>
                        {(() => {
                            let bulletOffset = 0;

                            return group.items.map((contentItem, idx) => {
                                const currentBulletOffset = bulletOffset;

                                if (contentItem?.blockType === "Bullet list") {
                                    bulletOffset += (contentItem?.blocks || []).filter(
                                        (item) => String(item?.item || item?.text || "").trim().length > 0
                                    ).length;
                                }

                                return (
                                    <div
                                        key={`${group.title}-item-${idx}`}
                                        className={`created-content-item${
                                            contentItem?.blockType === "Text" ||
                                            contentItem?.blockType === "Bullet list" ||
                                            contentItem?.blockType === "Progress bar"
                                                ? " with-border"
                                                : ""
                                        }`}
                                    >
                                        <div className="created-content-actions">
                                            <button
                                                type="button"
                                                className="content-action-btn"
                                                onClick={() => onEditContent?.(activeTab, contentItem.__contentIndex)}
                                            >
                                                <EditOutlined />
                                            </button>
                                            <Popconfirm
                                                title="Delete section content"
                                                description="Are you sure you want to delete this record?"
                                                okText="Yes"
                                                cancelText="No"
                                                onConfirm={() => onDeleteContent?.(contentItem?.contentId)}
                                                overlayStyle={{ fontSize: "12px" }}
                                                overlayClassName="delete-popconfirm-overlay"
                                            >
                                                <button
                                                    type="button"
                                                    className="content-action-btn delete"
                                                >
                                                    <DeleteOutlined />
                                                </button>
                                            </Popconfirm>
                                        </div>
                                        {renderBlockContent(contentItem, currentBulletOffset)}
                                    </div>
                                );
                            });
                        })()}
                    </div>
                ))}
            </div>

            <CustomButton type="primary" className="add-section-btn in-created-view" onClick={onAddSection}>
                + Add Section
            </CustomButton>

            <div className="created-actions-row">
                <CustomButton type="primary"  
            size="large" onClick={onProceed}>
                    Proceed
                </CustomButton>
            </div>
        </section>
    );
};

export default CreatedSectionView;
